import { useEffect, useState } from "react";
import { UserPlus, Mail, Pencil, Trash2, Link2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, Pill, Empty, inputCls, copyText } from "@/components/kit";
import { api, errMsg } from "@/lib/api";
import { FormDialog, SectionHead, Toggle } from "./common";

const BLANK = { name: "", email: "", role: "player", position: "", number: "", show_on_hub: true };

const MemberForm = ({ f, setF, editing }) => (
  <>
    <Field label="Name"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="member-name-input" /></Field>
    {!editing && <Field label="Email" hint="They'll receive a link to complete registration"><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="member-email-input" /></Field>}
    <div className="grid grid-cols-2 gap-4">
      <Field label="Role">
        <select className={inputCls} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="member-role-select">
          <option value="player">Player</option>
          <option value="admin">Team admin / staff</option>
        </select>
      </Field>
      <Field label="Squad no."><Input type="number" min="0" value={f.number ?? ""} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid="member-number-input" /></Field>
    </div>
    <Field label="Position / title"><Input value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} placeholder="e.g. Midfielder, Treasurer" data-testid="member-position-input" /></Field>
    <Toggle checked={f.show_on_hub} onChange={(v) => setF({ ...f, show_on_hub: v })} label="Show on public squad list" testid="member-show-hub-toggle" />
  </>
);

const MemberRow = ({ m, onEdit, onResend, onRemove }) => (
  <tr className="border-b border-white/5" data-testid={`member-row-${m.email}`}>
    <td className="py-3 pr-3 font-mono text-sm text-[#CCFF00]">{m.number ?? "–"}</td>
    <td className="py-3 pr-3"><p className="font-semibold text-white">{m.name}</p><p className="text-xs text-slate-500">{m.email}</p></td>
    <td className="py-3 pr-3 text-sm text-slate-300">{m.position}</td>
    <td className="py-3 pr-3"><Pill tone={m.role === "admin" ? "volt" : "cyan"}>{m.role}</Pill></td>
    <td className="py-3 pr-3"><Pill tone={m.status === "active" ? "green" : "amber"} data-testid={`member-status-${m.email}`}>{m.status === "active" ? "registered" : "invited"}</Pill></td>
    <td className="py-3 text-right">
      <div className="flex justify-end gap-1">
        {m.status === "invited" && <Button size="icon" variant="ghost" title="Resend invite" onClick={() => onResend(m)} data-testid={`resend-invite-${m.email}`}><Mail className="h-4 w-4" /></Button>}
        <Button size="icon" variant="ghost" title="Edit" onClick={() => onEdit(m)} data-testid={`edit-member-${m.email}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" title="Remove" className="text-rose-400" onClick={() => onRemove(m)} data-testid={`remove-member-${m.email}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </td>
  </tr>
);

export default function Squad({ teamId }) {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(BLANK);
  const [lastLink, setLastLink] = useState("");

  const load = () => api.get(`/teams/${teamId}/members`).then((r) => setMembers(r.data));
  useEffect(() => {
    load();
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const body = { ...f, number: f.number === "" || f.number === null ? null : Number(f.number) };
    try {
      if (editing) {
        const { email, ...rest } = body;
        await api.patch(`/teams/${teamId}/members/${editing.id}`, rest);
        toast.success("Member updated");
      } else {
        const { data } = await api.post(`/teams/${teamId}/members`, body);
        setLastLink(data.invite_url);
        toast.success(`Invite sent to ${data.email}`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const resend = async (m) => {
    try {
      const { data } = await api.post(`/teams/${teamId}/members/${m.id}/resend`);
      setLastLink(data.invite_url);
      toast.success("Invite re-sent");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team?`)) return;
    try {
      await api.delete(`/teams/${teamId}/members/${m.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6">
      <SectionHead title="Squad" text="Players and staff. New people get an email link to finish registering.">
        <Button className="rounded-full font-bold" onClick={() => { setEditing(null); setF(BLANK); setOpen(true); }} data-testid="add-member-btn"><UserPlus className="mr-1 h-4 w-4" /> Add member</Button>
      </SectionHead>
      {lastLink && (
        <Panel className="flex flex-wrap items-center justify-between gap-3 border-[#CCFF00]/30" data-testid="invite-link-panel">
          <p className="text-sm text-slate-300"><Link2 className="mr-1 inline h-4 w-4 text-[#CCFF00]" />Invite link (also emailed): <span className="break-all font-mono text-xs text-slate-400" data-testid="invite-link-text">{lastLink}</span></p>
          <Button size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent" onClick={() => copyText(lastLink)} data-testid="copy-invite-link-btn">Copy</Button>
        </Panel>
      )}
      <Panel className="overflow-x-auto">
        {members.length ? (
          <table className="w-full min-w-[680px] text-left">
            <thead><tr className="border-b border-white/10 font-mono text-[11px] uppercase tracking-wider text-slate-500"><th className="py-3">#</th><th>Name</th><th>Position</th><th>Role</th><th>Status</th><th /></tr></thead>
            <tbody>{members.map((m) => <MemberRow key={m.id} m={m} onEdit={(x) => { setEditing(x); setF({ ...x, number: x.number ?? "" }); setOpen(true); }} onResend={resend} onRemove={remove} />)}</tbody>
          </table>
        ) : <Empty icon={Users} title="No members yet" text="Add your first player to send them an invite." />}
      </Panel>
      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit member" : "Add member"} onSubmit={save} submitLabel={editing ? "Save changes" : "Send invite"} testid="member-form">
        <MemberForm f={f} setF={setF} editing={!!editing} />
      </FormDialog>
    </div>
  );
}
